import { Box, Typography, Container, Card, CardContent, Grid, Paper } from "@mui/material";
import { styled } from "@mui/material/styles";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

const HeroSection = styled(Box)(({ theme }) => ({
  position: "relative",
  minHeight: "50vh",
  height: "auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "white",
  backgroundImage: `url(${process.env.PUBLIC_URL}/savings-1.jpg)`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  padding: theme.spacing(2, 4),
  marginBottom: theme.spacing(8),

  // Create the black overlay
  "::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)", // Adjust opacity here (0.5 = 50% black)
    zIndex: 1,
  },

  // Ensure content appears above the overlay
  "& > *": {
    position: "relative",
    zIndex: 2,
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  overflow: "hidden",
  transition: "transform 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-5px)",
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 60,
  height: 60,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto",
  marginBottom: theme.spacing(2),
  background: theme.palette.background.paper,
  boxShadow: theme.shadows[3],
}));

export default function ResponsivePage() {
  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <Box sx={{ textAlign: "center", maxWidth: { xs: 300, md: 800 } }}>
          <IconWrapper>
            <AccountBalanceIcon color="primary" sx={{ fontSize: 30 }} />
          </IconWrapper>
          <Typography variant="h2" component="h1" gutterBottom sx={{ typography: { xs: "h4", md: "h2" } }}>
            Jana Nidhi
          </Typography>
          <Typography variant="h5" sx={{ typography: { xs: "body1", md: "h5" } }}>
            Empowering Youth Through Financial Inclusion
          </Typography>
        </Box>
      </HeroSection>

      <Container maxWidth="lg" sx={{ mb: 8 }}>
        {/* Main Content Card */}
        <StyledCard sx={{ mb: 6, mt: 8, position: "relative", zIndex: 1 }}>
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box
                  component="img"
                  src="/savings-3.png"
                  alt="Jana Nidhi Initiative"
                  sx={{
                    width: "100%",
                    height: "auto",
                    borderRadius: 2,
                    mb: { xs: 2, md: 0 },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: "bold", typography: { xs: "h5", md: "h4" } }}>
                  Rebuilding Through Youth Empowerment
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.6 }}>
                  With the objective of rebuilding the nation through youth empowerment, NYCS considers cooperatives as a model of business enterprise and is making every effort to promote entrepreneurship through the same. Financial activity is the mainstay of any business. In this direction, NYCS India has set up centres across the country under the name of Jana Nidhi to provide need-based finances to the youth for taking up income-generating activities and opportunities through self-employment and entrepreneurship.
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </StyledCard>

        {/* Why Microfinance Section */}
        <StyledCard>
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Box sx={{ maxWidth: 800, mx: "auto" }}>
              <Typography variant="h4" align="center" gutterBottom color="primary" sx={{ fontWeight: "bold", typography: { xs: "h5", md: "h4" }, mb: 3 }}>
                Why Microfinance?
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    bgcolor: "primary.main",
                    color: "white",
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Financial Inclusion
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                    Financial inclusion is generally defined as the availability of banking services at an affordable cost to disadvantaged and low-income groups. In India, the basic tool of financial inclusion is having a savings or current account with a bank.
                  </Typography>
                </Paper>

                <Paper elevation={3} sx={{ p: 3, borderRadius: 2, bgcolor: "background.paper" }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Key Questions We Address
                  </Typography>
                  <Box component="ul" sx={{ pl: 2 }}>
                    <Typography component="li" sx={{ mb: 1 }}>
                      How many underprivileged people actually hold a bank account?
                    </Typography>
                    <Typography component="li" sx={{ mb: 1 }}>
                      How many are encouraged to do so?
                    </Typography>
                    <Typography component="li">Even if they do hold an account, do they have access to credit and advisory facilities?</Typography>
                  </Box>
                </Paper>
              </Box>
            </Box>
          </CardContent>
        </StyledCard>
      </Container>
    </Box>
  );
}
